// Hook for messaging — sends/receives messages with 10s polling
import { useState, useEffect, useCallback, useRef } from "react";
import { ApiClient } from "@/services/api";

export interface Message {
  id: string;
  lead_id: string;
  sender_role: "agent" | "owner" | "system";
  content: string;
  created_at: string;
  read_at: string | null;
}

interface UseMessagesOptions {
  leadId: string | null;
  enabled?: boolean;
  pollInterval?: number;
}

interface UseMessagesReturn {
  messages: Message[];
  loading: boolean;
  sending: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<boolean>;
  markRead: () => Promise<void>;
  hasNewMessages: boolean;
  clearNewFlag: () => void;
}

export function useMessages({ leadId, enabled = true, pollInterval = 10_000 }: UseMessagesOptions): UseMessagesReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const prevCountRef = useRef(0);

  const fetchMessages = useCallback(async () => {
    if (!leadId || !enabled) return;
    try {
      const api = new ApiClient();
      const result = await api.get<{ data: Message[] }>("/messages", { lead_id: leadId });
      const newMessages = result.data;

      // Detect new messages for notification
      if (prevCountRef.current > 0 && newMessages.length > prevCountRef.current) {
        setHasNewMessages(true);
      }
      prevCountRef.current = newMessages.length;

      setMessages(newMessages);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  }, [leadId, enabled]);

  // Initial load
  useEffect(() => {
    if (!leadId || !enabled) {
      setMessages([]);
      return;
    }
    setLoading(true);
    fetchMessages().finally(() => setLoading(false));
  }, [leadId, enabled, fetchMessages]);

  // Polling
  useEffect(() => {
    if (!leadId || !enabled || pollInterval <= 0) return;
    const interval = setInterval(fetchMessages, pollInterval);
    return () => clearInterval(interval);
  }, [leadId, enabled, pollInterval, fetchMessages]);

  const sendMessage = useCallback(async (content: string): Promise<boolean> => {
    if (!leadId) return false;
    setSending(true);
    try {
      const api = new ApiClient();
      await api.post("/messages", { lead_id: leadId, content });
      await fetchMessages();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    } finally {
      setSending(false);
    }
  }, [leadId, fetchMessages]);

  const markRead = useCallback(async () => {
    if (!leadId) return;
    try {
      const api = new ApiClient();
      await api.patch("/messages/read", { lead_id: leadId });
      await fetchMessages();
    } catch {
      // non-critical
    }
  }, [leadId, fetchMessages]);

  const clearNewFlag = useCallback(() => setHasNewMessages(false), []);

  return { messages, loading, sending, error, sendMessage, markRead, hasNewMessages, clearNewFlag };
}

// Hook for unread counts across all leads
export function useUnreadCounts(enabled = true, pollInterval = 15_000) {
  const [counts, setCounts] = useState<{ per_lead: Record<string, number>; total: number }>({ per_lead: {}, total: 0 });

  const fetch = useCallback(async () => {
    if (!enabled) return;
    try {
      const api = new ApiClient();
      const result = await api.get<{ data: { per_lead: Record<string, number>; total: number } }>("/messages/unread-counts");
      setCounts(result.data);
    } catch {
      // non-critical
    }
  }, [enabled]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (!enabled || pollInterval <= 0) return;
    const interval = setInterval(fetch, pollInterval);
    return () => clearInterval(interval);
  }, [enabled, pollInterval, fetch]);

  return counts;
}
